import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, X, Plus, Minus, Trash2, ArrowLeft, Loader2, CheckCircle2, Star, Truck } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { eur, CONTACT, DELIVERY_PLZ } from "../lib/menuData";
import { fetchSpecialDays, specialFor, toMin } from "../lib/hours";
import { createOrder } from "../lib/api";

export const CartFab = () => {
  const { count, total, setOpen } = useCart();
  if (count === 0) return null;
  return (
    <motion.button
      initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
      data-testid="cart-fab" onClick={() => setOpen(true)}
      className="fixed z-[60] bottom-5 right-5 flex items-center gap-3 bg-gold text-teal-dark pl-5 pr-6 py-3.5 rounded-full font-medium shadow-2xl hover:bg-gold-light transition-colors"
    >
      <span className="relative">
        <ShoppingBag size={20} />
        <span data-testid="cart-count" className="absolute -top-2 -right-2 bg-teal-dark text-gold text-[0.65rem] h-5 w-5 flex items-center justify-center rounded-full font-bold">
          {count}
        </span>
      </span>
      <span className="hidden sm:inline">Warenkorb</span>
      <span className="font-serif text-lg">{eur(total)}</span>
    </motion.button>
  );
};

const inputCls =
  "w-full bg-cream/[0.04] border border-gold/25 rounded-sm px-4 py-3 text-cream placeholder-cream/35 focus:outline-none focus:border-gold transition-colors text-sm";

export const CartDrawer = () => {
  const { items, add, dec, remove, clear, count, subtotal, discount, total, open, setOpen } = useCart();
  const [step, setStep] = useState("cart");
  const [loading, setLoading] = useState(false);
  const [orderType, setOrderType] = useState("pickup");
  const [form, setForm] = useState({ name: "", phone: "", email: "", pickup_time: "", notes: "", street: "", plz: "", city: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const isDelivery = orderType === "delivery";
  const effTotal = total;
  const belowMin = isDelivery && subtotal < 20;
  const plzInvalid = isDelivery && form.plz.trim().length > 0 && !DELIVERY_PLZ.includes(form.plz.trim());
  const blocked = belowMin || plzInvalid;

  const [days, setDays] = useState([]);
  useEffect(() => { fetchSpecialDays().then(setDays); }, []);
  const blockedInfo = (() => {
    const now = new Date();
    const sp = specialFor(days, now);
    if (!sp) return null;
    if (sp.kind === "closed")
      return `Heute bleiben wir geschlossen${sp.note ? ` – ${sp.note}` : ""} – Bestellungen sind heute leider nicht möglich.`;
    const m = now.getHours() * 60 + now.getMinutes();
    let s = toMin(sp.open);
    let e = toMin(sp.close);
    if (e <= s) e += 24 * 60;
    if (!(s <= m && m < e))
      return `Heute sind Bestellungen von ${sp.open} bis ${sp.close} Uhr möglich${sp.note ? ` – ${sp.note}` : ""}.`;
    return null;
  })();
  const ordersBlocked = blockedInfo !== null;

  const close = () => { if (step === "done") clear(); setOpen(false); setStep("cart"); };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await createOrder({
        name: form.name, phone: form.phone, email: form.email || null,
        pickup_time: isDelivery ? "" : form.pickup_time, notes: form.notes,
        order_type: orderType,
        street: form.street, plz: form.plz, city: form.city,
        items: items.map((x) => ({ n: x.item.n, name: x.item.name, qty: x.qty })),
      });
      toast.success(
        isDelivery
          ? "Bestellung eingegangen! Wir liefern so schnell wie möglich."
          : "Bestellung eingegangen! Wir bereiten sie zur Abholung vor.",
        { icon: <CheckCircle2 size={18} /> }
      );
      setStep("done");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bestellung fehlgeschlagen. Bitte erneut versuchen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={close} className="fixed inset-0 z-[70] bg-teal-dark/70 backdrop-blur-sm" />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            data-testid="cart-drawer"
            className="fixed z-[80] top-0 right-0 h-full w-full sm:w-[440px] bg-teal-deep border-l border-gold/20 flex flex-col"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-gold/15">
              <div className="flex items-center gap-3">
                {step === "checkout" && (
                  <button data-testid="cart-back" onClick={() => setStep("cart")} className="text-gold hover:text-gold-light">
                    <ArrowLeft size={20} />
                  </button>
                )}
                <h3 className="font-serif text-2xl text-cream">
                  {step === "cart" ? "Dein Warenkorb" : step === "done" ? "Bestellung erhalten" : "Bestellung"}
                </h3>
              </div>
              <button data-testid="cart-close" onClick={close} className="text-cream/70 hover:text-cream"><X size={22} /></button>
            </div>

            {step === "done" ? (
              <div data-testid="order-success" className="flex-1 flex flex-col items-center justify-center px-8 text-center gap-5">
                <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 260, damping: 18 }}
                  className="h-20 w-20 rounded-full bg-gold/15 border border-gold/50 flex items-center justify-center text-gold">
                  <CheckCircle2 size={42} />
                </motion.div>
                <div>
                  <p className="font-serif text-2xl text-cream leading-snug">Danke für deine Bestellung!</p>
                  <p className="text-cream/60 text-sm mt-2 leading-relaxed">
                    {isDelivery
                      ? "Wir machen uns auf den Weg zu dir – bezahlt wird bei der Übergabe."
                      : "Wir bereiten alles frisch für dich vor – bezahlt wird bei der Abholung."}
                  </p>
                </div>
                <div className="w-full border-t border-gold/15 pt-5 space-y-3">
                  <p className="text-cream/55 text-xs leading-relaxed">
                    Hat es dir geschmeckt? Eine Google-Bewertung hilft uns enorm.
                  </p>
                  <a href={CONTACT.googleReviewUrl} target="_blank" rel="noopener noreferrer"
                    data-testid="google-review-btn"
                    className="w-full flex items-center justify-center gap-2 bg-gold text-teal-dark font-medium py-3.5 rounded-full hover:bg-gold-light transition-colors">
                    <Star size={18} /> Bewerte uns auf Google
                  </a>
                  <button data-testid="order-done-close" onClick={close}
                    className="w-full text-cream/60 hover:text-cream text-sm py-2 transition-colors">
                    Zurück zur Speisekarte
                  </button>
                </div>
              </div>
            ) : count === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-cream/50 gap-3">
                <ShoppingBag size={40} className="text-gold/40" />
                <p>Dein Warenkorb ist leer.</p>
              </div>
            ) : step === "cart" ? (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                  {items.map(({ item, qty }) => (
                    <div key={item.n} data-testid={`cart-line-${item.n}`} className="flex items-start gap-3 border-b border-gold/10 pb-4">
                      <div className="flex-1 min-w-0">
                        <div className="text-cream font-medium leading-tight">{item.name}</div>
                        <div className="text-gold-light text-sm mt-0.5">{eur(item.p)}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button data-testid={`cart-dec-${item.n}`} onClick={() => dec(item.n)} className="h-7 w-7 rounded-full border border-gold/30 text-gold flex items-center justify-center hover:bg-gold/10"><Minus size={14} /></button>
                        <span className="w-5 text-center text-cream">{qty}</span>
                        <button data-testid={`cart-inc-${item.n}`} onClick={() => add(item)} className="h-7 w-7 rounded-full border border-gold/30 text-gold flex items-center justify-center hover:bg-gold/10"><Plus size={14} /></button>
                        <button data-testid={`cart-remove-${item.n}`} onClick={() => remove(item.n)} className="ml-1 text-cream/40 hover:text-red-300"><Trash2 size={16} /></button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="px-6 py-5 border-t border-gold/15 space-y-3">
                  <p className="text-cream/55 text-xs">10 % Rabatt auf Abholung & Lieferung (ab 20 €) · Zahlung bei Übergabe (bar oder Karte)</p>
                  <div className="flex justify-between text-cream/70 text-sm">
                    <span>Zwischensumme</span>
                    <span data-testid="cart-subtotal">{eur(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-gold text-sm">
                    <span>Online-Rabatt (10 %)</span>
                    <span data-testid="cart-discount">−{eur(discount)}</span>
                  </div>
                  <div className="flex justify-between text-cream">
                    <span>Gesamt</span>
                    <span data-testid="cart-total" className="font-serif text-xl">{eur(total)}</span>
                  </div>
                  <button data-testid="cart-checkout-btn" onClick={() => setStep("checkout")}
                    className="w-full bg-gold text-teal-dark font-medium py-3.5 rounded-full hover:bg-gold-light transition-colors">
                    Zur Bestellung
                  </button>
                </div>
              </>
            ) : (
              <form onSubmit={submit} data-testid="checkout-form" className="flex-1 flex flex-col overflow-hidden">
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" data-testid="type-pickup" onClick={() => setOrderType("pickup")}
                      className={`flex items-center justify-center gap-2 rounded-full border py-3 text-sm font-medium transition-colors ${!isDelivery ? "bg-gold text-teal-dark border-gold" : "border-gold/30 text-cream/70 hover:border-gold/60"}`}>
                      <ShoppingBag size={16} /> Abholung
                    </button>
                    <button type="button" data-testid="type-delivery" onClick={() => setOrderType("delivery")}
                      className={`flex items-center justify-center gap-2 rounded-full border py-3 text-sm font-medium transition-colors ${isDelivery ? "bg-gold text-teal-dark border-gold" : "border-gold/30 text-cream/70 hover:border-gold/60"}`}>
                      <Truck size={16} /> Lieferung
                    </button>
                  </div>
                  <input required data-testid="co-name" className={inputCls} placeholder="Name" value={form.name} onChange={set("name")} />
                  <input required data-testid="co-phone" className={inputCls} placeholder="Telefonnummer" value={form.phone} onChange={set("phone")} />
                  <input type="email" data-testid="co-email" className={inputCls} placeholder="E-Mail (optional, für Bestätigung)" value={form.email} onChange={set("email")} />
                  {isDelivery ? (
                    <>
                      <input required data-testid="co-street" className={inputCls} placeholder="Straße & Hausnummer" value={form.street} onChange={set("street")} />
                      <div className="grid grid-cols-3 gap-2">
                        <input required data-testid="co-plz" className={inputCls} placeholder="PLZ" value={form.plz} onChange={set("plz")} />
                        <input required data-testid="co-city" className={`${inputCls} col-span-2`} placeholder="Ort" value={form.city} onChange={set("city")} />
                      </div>
                      {plzInvalid && (
                        <p data-testid="delivery-plz-hint" className="text-red-300 text-xs">
                          In diese Postleitzahl liefern wir leider nicht – du kannst aber gerne zur Abholung bestellen.
                        </p>
                      )}
                    </>
                  ) : (
                    <input data-testid="co-pickup-time" className={inputCls} placeholder="Wunsch-Abholzeit (optional, z. B. 19:00)" value={form.pickup_time} onChange={set("pickup_time")} />
                  )}
                  <textarea rows="2" data-testid="co-notes" className={inputCls} placeholder="Hinweis (optional)" value={form.notes} onChange={set("notes")} />
                  {isDelivery ? (
                    <p className="text-cream/50 text-xs">Wir liefern dir deine Bestellung <b className="text-cream/70">frisch nach Hause</b>. Bezahlt wird <b className="text-cream/70">bei Übergabe (bar oder Karte)</b> – inkl. <b className="text-gold">10 % Online-Rabatt</b>. Mindestbestellwert: <b className="text-cream/70">20 €</b>.</p>
                  ) : (
                    <p className="text-cream/50 text-xs">Deine Bestellung wird zur <b className="text-cream/70">Abholung</b> in der Wilhelmstraße 43 vorbereitet. Bezahlt wird <b className="text-cream/70">vor Ort (bar oder Karte)</b> – inkl. <b className="text-gold">10 % Online-Rabatt</b>.</p>
                  )}
                </div>
                <div className="px-6 py-5 border-t border-gold/15 space-y-3">
                  <div className="flex justify-between text-cream/70 text-sm">
                    <span>Zwischensumme</span><span>{eur(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-gold text-sm">
                    <span>Online-Rabatt (10 %)</span><span>−{eur(discount)}</span>
                  </div>
                  <div className="flex justify-between text-cream">
                    <span>Gesamt</span><span data-testid="checkout-total" className="font-serif text-xl">{eur(effTotal)}</span>
                  </div>
                  {ordersBlocked && (
                    <p data-testid="orders-blocked-hint" className="text-gold text-xs">
                      {blockedInfo}
                    </p>
                  )}
                  {belowMin && (
                    <p data-testid="delivery-min-hint" className="text-red-300 text-xs">
                      Mindestbestellwert für Lieferung: 20 € – dir fehlen noch {eur(20 - subtotal)}.
                    </p>
                  )}
                  <button type="submit" disabled={loading || blocked || ordersBlocked} data-testid="place-order-btn"
                    className="w-full flex items-center justify-center gap-2 bg-gold text-teal-dark font-medium py-3.5 rounded-full hover:bg-gold-light transition-colors disabled:opacity-50">
                    {loading ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />} {isDelivery ? "Lieferung bestellen" : "Bestellung aufgeben"}
                  </button>
                </div>
              </form>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
